<script setup lang="ts">
import { ref, onMounted, computed, getCurrentInstance } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import ParticipantManagementModal from './ParticipantManagementModal.vue';
import TournamentResultsModal from '@tournaments/TournamentResultsModal.vue';
import Avatar from '../shared/Avatar.vue';

interface Props {
  event?: any;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  join: [event: any];
}>();

const route = useRoute();
const { t, locale } = useI18n();
const event = ref(props.event);
const isLoading = ref(!props.event);
const error = ref<string | null>(null);
const instance = getCurrentInstance();

const showParticipantModal = ref(false);
const showTournamentResultsModal = ref(false);
const isCancelling = ref(false);
const isJoining = ref(false);
const isLeaving = ref(false);

// Computed properties
const isTournament = computed(() => {
  return event.value?.attributes?.event_type === 3 || event.value?.attributes?.event_type === 'tournament';
});

// Get localized event type name
const eventTypeName = computed(() => {
  const eventTypeKey = event.value?.attributes?.event_type_key;
  if (!eventTypeKey) {
    // Fallback to original name if event_type_key not available
    return event.value?.attributes?.event_type_name || '';
  }

  return t(`activerecord.attributes.event.event_types.${eventTypeKey}`);
});

const formatDate = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);

  // Check if date is valid (not NaN or Invalid Date)
  if (isNaN(date.getTime())) return '';

  const dateLocale = locale.value === 'ru' ? 'ru-RU' : 'en-US';
  return date.toLocaleDateString(dateLocale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatPrice = (price: number) => {
  if (!price) return t('events.price_free');
  const numberLocale = locale.value === 'ru' ? 'ru-RU' : 'en-US';
  return `${(price / 100).toLocaleString(numberLocale)} ₽`;
};

const getEventStatus = () => {
  const status = event.value?.attributes?.status;
  switch (status) {
    case 'draft':
      return { text: t('events.status.draft'), class: 'status-draft' };
    case 'active':
      return { text: t('events.status.active'), class: 'status-published' };
    case 'finished':
      return { text: t('events.status.finished'), class: 'status-finished' };
    case 'expired':
      return { text: t('events.status.expired'), class: 'status-expired' };
    case 'cancelled':
      return { text: t('events.status.cancelled'), class: 'status-cancelled' };
    case 'archived':
      return { text: t('events.status.archived'), class: 'status-archived' };
    default:
      return { text: t('events.status.unknown'), class: 'status-unknown' };
  }
};

// Fetch event data if not provided as prop
const fetchEvent = async (eventId: string | number) => {
  try {
    isLoading.value = true;
    error.value = null;

    const response = await axios.get(`/api/v1/events/${eventId}`);
    event.value = response.data.data || response.data;
  } catch (err) {
    console.error('Error fetching event:', err);
    error.value = t('events.full.error_loading_event');
  } finally {
    isLoading.value = false;
  }
};

const openParticipantModal = () => {
  showParticipantModal.value = true;
};

const closeParticipantModal = () => {
  showParticipantModal.value = false;
};

const handleParticipantModalClose = () => {
  closeParticipantModal();
};



const openTournamentResultsModal = () => {
  showTournamentResultsModal.value = true;
};

const closeTournamentResultsModal = () => {
  showTournamentResultsModal.value = false;
};

const cancelEvent = async () => {
  if (!confirm(t('events.full.confirm.cancel_event'))) {
    return;
  }

  try {
    isCancelling.value = true;
    const response = await axios.post(`/api/v1/events/${event.value.id}/cancel`);

    if (response.data.success) {
      // Update event status locally
      event.value.attributes.status = 'cancelled';

      // Show success message
      const createToast = instance?.appContext?.app?.config?.globalProperties?.$toast;
      if (createToast) {
        createToast(
          {
            title: t('events.full.toast.cancelled.title'),
            description: t('events.full.toast.cancelled.description'),
            type: 'success'
          },
          {
            timeout: 5000,
            position: 'bottom-center',
            dangerouslyUseHTMLString: true
          }
        );
      }
    }
    } catch (error: any) {
    console.error('Error cancelling event:', error);

    // Show error message
    const createToast = instance?.appContext?.app?.config?.globalProperties?.$toast;
    if (createToast) {
      createToast(
        {
          title: t('events.full.toast.error_title'),
          description: error.response?.data?.error || t('events.full.toast.cancel_error'),
          type: 'error'
        },
        {
          timeout: 5000,
          position: 'bottom-center',
          dangerouslyUseHTMLString: true
        }
      );
    }
  } finally {
    isCancelling.value = false;
  }
};

const joinEvent = async () => {
  try {
    isJoining.value = true;
    const response = await axios.post(`/api/v1/events/${event.value.id}/join`);

    if (response.data.success) {
      // Update event data locally
      event.value.attributes.joined = true;
      event.value.attributes.joinable = false;

      // Show success message
      const createToast = instance?.appContext?.app?.config?.globalProperties?.$toast;
      if (createToast) {
        createToast(
          {
            title: t('events.full.toast.joined.title'),
            description: t('events.full.toast.joined.description'),
            type: 'success'
          },
          {
            timeout: 5000,
            position: 'bottom-center',
            dangerouslyUseHTMLString: true
          }
        );
      }
    }
  } catch (error: any) {
    console.error('Error joining event:', error);

    // Show error message
    const createToast = instance?.appContext?.app?.config?.globalProperties?.$toast;
    if (createToast) {
      createToast(
        {
          title: t('events.full.toast.error_title'),
          description: error.response?.data?.error || t('events.full.toast.join_error'),
          type: 'error'
        },
        {
          timeout: 5000,
          position: 'bottom-center',
          dangerouslyUseHTMLString: true
        }
      );
    }
  } finally {
    isJoining.value = false;
  }
};

const leaveEvent = async () => {
  if (!confirm(t('events.full.confirm.leave_event'))) {
    return;
  }

  try {
    isLeaving.value = true;
    const response = await axios.post(`/api/v1/events/${event.value.id}/leave`);

    if (response.data.success) {
      // Update event data locally
      event.value.attributes.joined = false;
      event.value.attributes.joinable = true;

      // Show success message
      const createToast = instance?.appContext?.app?.config?.globalProperties?.$toast;
      if (createToast) {
        createToast(
          {
            title: t('events.full.toast.left.title'),
            description: t('events.full.toast.left.description'),
            type: 'success'
          },
          {
            timeout: 5000,
            position: 'bottom-center',
            dangerouslyUseHTMLString: true
          }
        );
      }
    }
  } catch (error: any) {
    console.error('Error leaving event:', error);

    // Show error message
    const createToast = instance?.appContext?.app?.config?.globalProperties?.$toast;
    if (createToast) {
      createToast(
        {
          title: t('events.full.toast.error_title'),
          description: error.response?.data?.error || t('events.full.toast.leave_error'),
          type: 'error'
        },
        {
          timeout: 5000,
          position: 'bottom-center',
          dangerouslyUseHTMLString: true
        }
      );
    }
  } finally {
    isLeaving.value = false;
  }
};

onMounted(() => {
  if (!props.event && route.params.id) {
    const eventId = Array.isArray(route.params.id) ? route.params.id[0] : route.params.id;
    if (/^\d+$/.test(String(eventId))) {
      fetchEvent(eventId);
    } else {
      error.value = t('events.full.error_loading_event');
      isLoading.value = false;
    }
  }
});
</script>

<template>
  <div v-if="isLoading" class="event-loading">
    <div class="loading-spinner">
      <div class="spinner"></div>
    </div>
    <p>{{ t('events.full.loading_event') }}</p>
  </div>

  <div v-else-if="error" class="event-error">
    <div class="error-icon">⚠️</div>
    <h2>{{ t('events.full.error_loading_title') }}</h2>
    <p>{{ error }}</p>
    <button @click="fetchEvent(Array.isArray(route.params.id) ? route.params.id[0] : route.params.id)" class="btn btn-primary">
      {{ t('events.full.retry') }}
    </button>
  </div>

  <div v-else-if="event" class="event-show">
    <!-- Event Header -->
    <div class="event-header">
      <div class="event-header-content">
        <div class="event-avatar-section">
          <Avatar
            :src="event.attributes.avatar_url"
            :alt="event.attributes.name"
            size="large"
            :show-frame="true"
            shape="hexagon"
            fallback-icon="bi-calendar-event"
          />
        </div>
        <div class="event-header-info">
          <div class="event-badges">
            <span :class="['event-type-badge', { 'tournament-badge': isTournament }]">
              {{ eventTypeName }}
            </span>
            <span :class="['event-status-badge', getEventStatus().class]">
              {{ getEventStatus().text }}
            </span>
          </div>

          <h1 class="event-title">{{ event.attributes.name }}</h1>

          <div class="event-meta">
            <div class="event-location">
              <i class="bi bi-geo-alt"></i>
              <span>{{ event.attributes.human_city || t('events.full.city_not_specified') }}</span>
            </div>
            <div v-if="event.attributes.address" class="event-address">
              <i class="bi bi-pin-map"></i>
              <span>{{ event.attributes.address }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Event Content -->
    <div class="event-content">
      <div class="event-main">
        <!-- Event Description -->
        <div v-if="event.attributes.description" class="event-description">
          <h3>{{ t('events.full.sections.description') }}</h3>
          <div v-html="event.attributes.description" class="event-description-content"></div>
        </div>

        <!-- Event Details -->
        <div class="event-details">
          <h3>{{ t('events.full.details.title') }}</h3>
          <div class="details-grid">
            <div class="detail-item">
              <div class="detail-icon">
                <i class="bi bi-calendar-event"></i>
              </div>
              <div class="detail-content">
                <div class="detail-label">{{ t('events.form.fields.date_time') }}</div>
                <div class="detail-value">
                  {{ formatDate(event.attributes.starts_at) }}
                  <span v-if="event.attributes.ends_at" class="detail-end">
                    — {{ formatDate(event.attributes.ends_at) }}
                  </span>
                </div>
              </div>
            </div>

            <div class="detail-item">
              <div class="detail-icon">
                <i class="bi bi-people"></i>
              </div>
              <div class="detail-content">
                <div class="detail-label">{{ t('events.full.details.participants') }}</div>
                <div class="detail-value">
                  {{ event.attributes.approved_participant_count || 0 }} / {{ event.attributes.max_players }}
                </div>
              </div>
            </div>

            <div class="detail-item">
              <div class="detail-icon">
                <i class="bi bi-coin"></i>
              </div>
              <div class="detail-content">
                <div class="detail-label">{{ t('events.full.details.price') }}</div>
                <div class="detail-value">{{ formatPrice(event.attributes.price) }}</div>
              </div>
            </div>

            <div class="detail-item">
              <div class="detail-icon">
                <i class="bi bi-card-list"></i>
              </div>
              <div class="detail-content">
                <div class="detail-label">{{ t('events.full.details.roster_format') }}</div>
                <div class="detail-value">{{ event.attributes.roster_format }} {{ t('structure.points') }}</div>
              </div>
            </div>

            <div v-if="event.attributes.social_links" class="detail-item">
              <div class="detail-icon">
                <i class="bi bi-link-45deg"></i>
              </div>
              <div class="detail-content">
                <div class="detail-label">{{ t('events.form.fields.social_links') }}</div>
                <div class="detail-value">
                  <a :href="event.attributes.social_links" target="_blank" class="social-link">
                    {{ event.attributes.social_links }}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Event Actions -->
      <div class="event-actions">
        <!-- User Actions -->
        <div class="action-section">
          <h3>{{ t('events.full.sections.participation') }}</h3>

          <div v-if="event.attributes.joinable" class="action-buttons">
            <button @click="joinEvent" :disabled="isJoining" class="btn btn-primary btn-large">
              <i class="bi bi-person-plus"></i>
              {{ isJoining ? t('events.full.actions.joining') : t('events.participate') }}
            </button>
          </div>

          <div v-else-if="event.attributes.joined" class="joined-status">
            <div class="status-icon">
              <i class="bi bi-check-circle"></i>
            </div>
            <div class="status-content">
              <div class="status-title">{{ t('events.full.status.joined_title') }}</div>
              <div class="status-subtitle">{{ t('events.full.status.joined_subtitle') }}</div>
            </div>
            <div class="action-buttons">
              <button @click="leaveEvent" :disabled="isLeaving" class="btn btn-outline-danger">
                <i class="bi bi-person-dash"></i>
                {{ isLeaving ? t('events.full.actions.leaving') : t('events.full.actions.leave') }}
              </button>
            </div>
          </div>

          <div v-else class="not-joinable">
            <div class="status-icon">
              <i class="bi bi-x-circle"></i>
            </div>
            <div class="status-content">
              <div class="status-title">{{ t('events.full.status.not_joinable_title') }}</div>
              <div class="status-subtitle">{{ t('events.full.status.not_joinable_subtitle') }}</div>
            </div>
          </div>
        </div>

        <!-- Tournament Results -->
        <div v-if="isTournament && (event.attributes.joined || event.attributes.approved_participant_count > 0)" class="action-section">
          <h3>{{ t('events.full.sections.tournament') }}</h3>
          <div class="action-buttons">
            <button @click="openTournamentResultsModal" class="btn btn-info">
              <i class="bi bi-trophy"></i>
              {{ t('events.full.actions.tournament_results') }}
            </button>
          </div>
        </div>

        <!-- Admin Actions -->
        <div v-if="event.attributes.editable" class="action-section admin-actions">
          <h3>{{ t('events.full.sections.management') }}</h3>
          <div class="action-buttons">
            <router-link :to="{ name: 'edit_event', params: { id: event.id } }" class="btn btn-secondary">
              <i class="bi bi-pencil"></i>
              {{ t('events.edit') }}
            </router-link>
            <button @click="openParticipantModal" class="btn btn-secondary">
              <i class="bi bi-people"></i>
              {{ t('events.manage_participants') }}
            </button>
            <router-link
              v-if="isTournament"
              :to="{ name: 'tournament_management', params: { id: event.id } }"
              class="btn btn-secondary"
            >
              <i class="bi bi-gear"></i>
              {{ t('events.manage_tournament') }}
            </router-link>
            <button
              v-if="!event.attributes.cancelled && !event.attributes.expired"
              @click="cancelEvent"
              :disabled="isCancelling"
              class="btn btn-danger"
            >
              <i class="bi bi-x-circle"></i>
              {{ isCancelling ? t('events.full.actions.cancelling') : t('events.full.actions.cancel_event') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <ParticipantManagementModal
    v-if="event"
    :event-id="Number(event.id)"
    :is-visible="showParticipantModal"
    @close="handleParticipantModalClose"
  />



  <TournamentResultsModal
    v-if="event"
    :event-id="Number(event.id)"
    :is-visible="showTournamentResultsModal"
    @close="closeTournamentResultsModal"
  />
</template>

<style lang="scss" scoped>
// Color Variables
$primary-bg: #efd8b0;
$primary-text: #bd9c65;
$icon-bg: #c29c58;
$icon-text: #60451f;
$hover-bg: #b08f4f;
$border-color: #bd9c65;
$focus-shadow: rgba(189, 156, 101, 0.2);
$tournament-bg: #e8f5e8;
$tournament-border: #4caf50;
$tournament-text: #2e7d32;
$success-color: #4caf50;
$error-color: #f44336;
$warning-color: #ff9800;

// Loading and Error States
.event-loading, .event-error {
  text-align: center;
  padding: 60px 20px;
  color: $icon-text;
}

.loading-spinner {
  margin-bottom: 20px;

  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid $border-color;
    border-top: 4px solid $icon-bg;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto;
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-icon {
  font-size: 3em;
  margin-bottom: 20px;
}

// Event Show Layout
.event-show {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

// Event Header
.event-header {
  background: linear-gradient(135deg, $primary-bg 0%, #f5e6c3 100%);
  border-radius: 16px;
  padding: 40px;
  margin-bottom: 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border: 1px solid $border-color;
}

.event-header-content {
  display: flex;
  align-items: center;
  gap: 30px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }
}

.event-avatar-section {
  flex-shrink: 0;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid $border-color;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
  }
}



.event-header-info {
  flex: 1;
}

.event-badges {
  display: flex;
  justify-content: flex-start;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.event-type-badge {
  background-color: $icon-bg;
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &.tournament-badge {
    background-color: $tournament-border;
  }
}

.event-status-badge {
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: bold;

  &.status-draft {
    background-color: $warning-color;
    color: white;
  }

  &.status-published {
    background-color: $success-color;
    color: white;
  }

  &.status-finished {
    background-color: #4caf50;
    color: white;
  }

  &.status-cancelled {
    background-color: $error-color;
    color: white;
  }

  &.status-expired {
    background-color: #9e9e9e;
    color: white;
  }

  &.status-archived {
    background-color: #607d8b;
    color: white;
  }

  &.status-unknown {
    background-color: #9e9e9e;
    color: white;
  }
}

.event-title {
  font-size: 2.5em;
  font-weight: bold;
  color: $icon-text;
  margin: 20px 0;
  line-height: 1.2;
}

.event-meta {
  display: flex;
  justify-content: flex-start;
  gap: 30px;
  flex-wrap: wrap;
  margin-top: 20px;
}

.event-location, .event-address {
  display: flex;
  align-items: center;
  gap: 8px;
  color: $primary-text;
  font-size: 16px;
  font-weight: 500;

  i {
    color: $icon-bg;
    font-size: 18px;
  }
}

// Event Content
.event-content {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.event-main {
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border: 1px solid $border-color;
}

.event-description {
  margin-bottom: 30px;

  h3 {
    color: $icon-text;
    font-size: 1.5em;
    margin-bottom: 15px;
    border-bottom: 2px solid $border-color;
    padding-bottom: 10px;
  }

  .event-description-content {
    color: $primary-text;
    line-height: 1.6;
    font-size: 16px;

    :deep(p) {
      margin: 0.5em 0;
    }

    :deep(h1),
    :deep(h2),
    :deep(h3) {
      margin: 0.5em 0;
      font-weight: bold;
    }

    :deep(ul),
    :deep(ol) {
      padding-left: 1.5em;
      margin: 0.5em 0;
    }

    :deep(strong) {
      font-weight: bold;
    }

    :deep(em) {
      font-style: italic;
    }

    :deep(code) {
      background-color: rgba(0, 0, 0, 0.1);
      padding: 2px 4px;
      border-radius: 3px;
      font-family: monospace;
    }

    :deep(blockquote) {
      border-left: 3px solid $border-color;
      padding-left: 1em;
      margin: 0.5em 0;
      font-style: italic;
    }
  }
}

.event-details {
  h3 {
    color: $icon-text;
    font-size: 1.5em;
    margin-bottom: 20px;
    border-bottom: 2px solid $border-color;
    padding-bottom: 10px;
  }
}

.details-grid {
  display: grid;
  gap: 20px;
}

.detail-item {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  padding: 20px;
  background: $primary-bg;
  border-radius: 8px;
  border: 1px solid $border-color;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
}

.detail-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: $icon-bg;
  color: white;
  border-radius: 50%;
  flex-shrink: 0;

  i {
    font-size: 18px;
  }
}

.detail-content {
  flex: 1;
}

.detail-label {
  font-size: 14px;
  color: $primary-text;
  font-weight: 500;
  margin-bottom: 5px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 16px;
  color: $icon-text;
  font-weight: bold;
}

.detail-end {
  color: $primary-text;
  font-weight: normal;
  margin-left: 8px;
}

.social-link {
  color: $icon-bg;
  text-decoration: none;
  word-break: break-all;

  &:hover {
    text-decoration: underline;
  }
}

// Event Actions
.event-actions {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.action-section {
  background: white;
  border-radius: 12px;
  padding: 25px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border: 1px solid $border-color;

  h3 {
    color: $icon-text;
    font-size: 1.3em;
    margin-bottom: 20px;
    border-bottom: 2px solid $border-color;
    padding-bottom: 10px;
  }
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &.btn-primary {
    background: $icon-bg;
    color: white;

    &:hover {
      background: $hover-bg;
    }
  }

  &.btn-secondary {
    background: $primary-bg;
    color: $icon-text;
    border: 1px solid $border-color;

    &:hover {
      background: #e6d4a0;
    }
  }

  &.btn-info {
    background: $tournament-border;
    color: white;

    &:hover {
      background: #45a049;
    }
  }

  &.btn-danger {
    background: $error-color;
    color: white;

    &:hover {
      background: #d32f2f;
    }

    &:disabled {
      background: #ccc;
      cursor: not-allowed;
    }
  }

  &.btn-outline-danger {
    background: transparent;
    color: $error-color;
    border: 2px solid $error-color;

    &:hover {
      background: $error-color;
      color: white;
    }

    &:disabled {
      background: #ccc;
      border-color: #ccc;
      color: #999;
      cursor: not-allowed;
    }
  }

  &.btn-large {
    padding: 16px 24px;
    font-size: 18px;
  }

  i {
    font-size: 18px;
  }
}

// Status displays
.joined-status, .not-joinable {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid $border-color;
}

.joined-status {
  background: linear-gradient(135deg, $success-color 0%, #66bb6a 100%);
  color: white;
}

.not-joinable {
  background: linear-gradient(135deg, $error-color 0%, #ef5350 100%);
  color: white;
}

.status-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;

  i {
    font-size: 24px;
  }
}

.status-content {
  flex: 1;
}

.status-title {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 5px;
}

.status-subtitle {
  font-size: 14px;
  opacity: 0.9;
}

// Admin actions styling
.admin-actions {
  border-left: 4px solid $icon-bg;
}

// Responsive design
@media (max-width: 768px) {
  .event-show {
    padding: 10px;
  }

  .event-header {
    padding: 30px 20px;
  }

  .event-header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }

  .event-avatar-section {
    width: 100px;
    height: 100px;
  }

  .event-title {
    font-size: 2em;
  }

  .event-meta {
    flex-direction: column;
    gap: 15px;
  }

  .event-content {
    gap: 20px;
  }

  .event-main, .action-section {
    padding: 20px;
  }
}
</style>
